import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix broken default Leaflet icon URLs in webpack builds
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl:       require('leaflet/dist/images/marker-icon.png'),
  shadowUrl:     require('leaflet/dist/images/marker-shadow.png'),
});

// ─── Unit type colour / icon palette ──────────────────────────────────────────
const UNIT_TYPE_COLOR = {
  watershed:           '#0ea5e9',
  harvesting_zone:     '#22c55e',
  ecological_corridor: '#84cc16',
  parcel:              '#94a3b8',
  cultural_area:       '#a855f7',
  seasonal_zone:       '#f59e0b',
  project_footprint:   '#ef4444',
  custom:              '#64748b',
};

const UNIT_TYPE_EMOJI = {
  watershed:           '💧',
  harvesting_zone:     '🌿',
  ecological_corridor: '🦅',
  parcel:              '📐',
  cultural_area:       '🏛️',
  seasonal_zone:       '🍂',
  project_footprint:   '🏗️',
  custom:              '📍',
};

const UNIT_TYPE_LABEL = {
  watershed:           'Watershed',
  harvesting_zone:     'Harvesting Zone',
  ecological_corridor: 'Ecological Corridor',
  parcel:              'Parcel',
  cultural_area:       'Cultural Area',
  seasonal_zone:       'Seasonal Zone',
  project_footprint:   'Project Footprint',
  custom:              'Custom',
};

// ─── Custom emoji marker ──────────────────────────────────────────────────────
function makeIcon(emoji, color, selected) {
  const size = selected ? 30 : 24;
  return L.divIcon({
    className: '',
    html: `<div style="font-size:${selected ? 26 : 20}px;line-height:1;
      filter:drop-shadow(0 1px 3px rgba(0,0,0,0.7));
      text-shadow:0 0 10px ${color};
      ${selected ? 'transform:scale(1.25);display:inline-block;' : ''}
    ">${emoji}</div>`,
    iconSize:    [size, size],
    iconAnchor:  [size / 2, size / 2],
    popupAnchor: [0, -size / 2 - 4],
  });
}

// ─── Auto-fit bounds on units change ─────────────────────────────────────────
function BoundsUpdater({ units }) {
  const map = useMap();
  useEffect(() => {
    const pts = [];
    units.forEach(u => {
      if (!u.geometry) return;
      if (u.geometry.type === 'Point') {
        const [lng, lat] = u.geometry.coordinates;
        pts.push([lat, lng]);
      } else if (u.geometry.type === 'Polygon') {
        (u.geometry.coordinates[0] || []).forEach(([lng, lat]) => pts.push([lat, lng]));
      } else if (u.geometry.type === 'LineString') {
        u.geometry.coordinates.forEach(([lng, lat]) => pts.push([lat, lng]));
      }
    });
    if (pts.length > 0) map.fitBounds(pts, { padding: [40, 40], maxZoom: 13 });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units]);
  return null;
}

// ─── LandUnitMap ──────────────────────────────────────────────────────────────
/**
 * Props:
 *   units        — array of LandUnit documents (with geometry field)
 *   selectedUnit — the currently selected unit (or null)
 *   onUnitClick  — (unit) => void  — callback when user clicks "View Details"
 *   height       — CSS height string (default '420px')
 *   center       — [lat, lng]  (default [56, -120])
 *   zoom         — number (default 5)
 */
export default function LandUnitMap({
  units = [],
  selectedUnit = null,
  onUnitClick,
  height = '420px',
  center = [56, -120],
  zoom   = 5,
}) {
  return (
    <div style={{ position: 'relative', height, borderRadius: 10, overflow: 'hidden', border: '1px solid #1e293b' }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%', background: '#0f172a' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
        />
        <BoundsUpdater units={units} />

        {units.map(u => {
          if (!u.geometry) return null;

          const color      = UNIT_TYPE_COLOR[u.unitType] || '#94a3b8';
          const emoji      = UNIT_TYPE_EMOJI[u.unitType] || '📍';
          const label      = UNIT_TYPE_LABEL[u.unitType] || u.unitType;
          const isSelected = selectedUnit?._id === u._id;
          const scopeColor = u.scope === 'platform' ? '#a855f7' : '#0ea5e9';

          const popup = (
            <Popup>
              <div style={{ minWidth: 180 }}>
                <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 14 }}>
                  {emoji} {u.name}
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                  {label}
                  <span style={{ marginLeft: 8, color: scopeColor }}>● {u.scope}</span>
                </div>
                {u.areaHa != null && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                    {u.areaHa.toLocaleString()} ha
                  </div>
                )}
                {u.description && (
                  <p style={{ fontSize: 12, margin: '4px 0 8px', color: '#475569' }}>
                    {u.description}
                  </p>
                )}
                {onUnitClick && (
                  <button
                    onClick={() => onUnitClick(u)}
                    style={{
                      background: color, border: 'none', color: '#fff',
                      borderRadius: 4, padding: '4px 12px', cursor: 'pointer', fontSize: 12,
                    }}
                  >
                    View Details
                  </button>
                )}
              </div>
            </Popup>
          );

          if (u.geometry.type === 'Point') {
            const [lng, lat] = u.geometry.coordinates;
            return (
              <Marker key={u._id} position={[lat, lng]} icon={makeIcon(emoji, color, isSelected)}>
                {popup}
              </Marker>
            );
          }

          if (u.geometry.type === 'Polygon') {
            const positions = (u.geometry.coordinates[0] || []).map(([lng, lat]) => [lat, lng]);
            return (
              <Polygon
                key={u._id}
                positions={positions}
                pathOptions={{
                  color,
                  fillColor: color,
                  fillOpacity: isSelected ? 0.4 : 0.18,
                  weight: isSelected ? 3 : 2,
                  opacity: isSelected ? 1 : 0.8,
                }}
              >
                {popup}
              </Polygon>
            );
          }

          if (u.geometry.type === 'LineString') {
            const positions = u.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
            return (
              <Polyline
                key={u._id}
                positions={positions}
                pathOptions={{ color, weight: isSelected ? 5 : 3, opacity: isSelected ? 1 : 0.8 }}
              >
                {popup}
              </Polyline>
            );
          }

          return null;
        })}
      </MapContainer>

      {/* ── Legend overlay ────────────────────────────────────────────────── */}
      <div style={{
        position: 'absolute', bottom: 28, right: 8, zIndex: 1000,
        background: 'rgba(15,23,42,0.90)', border: '1px solid #1e3a5f',
        borderRadius: 8, padding: '8px 12px', minWidth: 160,
        backdropFilter: 'blur(4px)',
      }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: '#475569',
          letterSpacing: 1, marginBottom: 6 }}>UNIT TYPES</div>
        {Object.entries(UNIT_TYPE_LABEL).map(([type, label]) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center',
            gap: 6, marginBottom: 3 }}>
            <span style={{ fontSize: 12 }}>{UNIT_TYPE_EMOJI[type]}</span>
            <span style={{
              width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
              background: UNIT_TYPE_COLOR[type],
            }} />
            <span style={{ fontSize: 10, color: '#94a3b8' }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
